/**
 * posDiagnostics — module-level mutable state that both the auto-print
 * controller and the new-order poller write to, and the POS Printer tab
 * subscribes to for a live diagnostics readout.
 *
 * Deliberately a plain object + subscribe pattern (not Redux/Zustand)
 * because it must be:
 *   - a true singleton (no React lifecycle involvement)
 *   - readable from non-React code (the auto-print controller)
 *   - safe to mutate from any interval / callback without triggering
 *     unrelated React re-renders
 *
 * NO customer PII is stored here — only order IDs, timestamps, and status.
 */

const state = {
  services: {
    posBgMounted: false,
    autoPrintRunning: false,
    newOrderPollerRunning: false,
    androidBridgeAvailable: false,
    soundState: 'IDLE', // IDLE | READY | BLOCKED | ERROR
  },
  newOrderPoll: {
    lastPollAt: null,
    lastHttpStatus: null,
    lastCount: null,
    lastError: null,
    lastChimeAt: null,
  },
  autoPrint: {
    lastPollAt: null,
    lastHttpStatus: null,
    lastPrintableCount: null,
    lastOrderDetected: null,
    lastPrintAttemptAt: null,
    lastPrintAttemptOrder: null,
    lastPrintResult: 'NONE', // NONE | SUCCESS | FAILED
    lastPrintError: null,
  },
  resume: {
    lastResumeAt: null,
    lastResumeReason: null,
  },
};

const subscribers = new Set();
// Cached snapshot — useSyncExternalStore requires that consecutive calls
// to getSnapshot() return referentially-equal results when nothing has
// changed, otherwise React enters an infinite render loop. We rebuild
// the snapshot inside notify() only.
let cachedSnapshot = null;
const buildSnapshot = () => ({
  services: { ...state.services },
  newOrderPoll: { ...state.newOrderPoll },
  autoPrint: { ...state.autoPrint },
  resume: { ...state.resume },
});
cachedSnapshot = buildSnapshot();

const notify = () => {
  cachedSnapshot = buildSnapshot();
  for (const cb of subscribers) {
    try { cb(); } catch (_e) { /* ignore subscriber errors */ }
  }
};

/** Return the cached snapshot — same object reference between changes. */
export const getSnapshot = () => cachedSnapshot;

export const subscribe = (cb) => {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
};

// ---------------------------------------------------------------------
// Setters — deliberately small and specific so hot paths don't have to
// destructure the whole state object.
// ---------------------------------------------------------------------
export const setBgMounted = (v) => { state.services.posBgMounted = v; notify(); };
export const setAutoPrintRunning = (v) => { state.services.autoPrintRunning = v; notify(); };
export const setNewOrderPollerRunning = (v) => { state.services.newOrderPollerRunning = v; notify(); };
export const setBridgeAvailable = (v) => { state.services.androidBridgeAvailable = v; notify(); };
export const setSoundState = (v) => { state.services.soundState = v; notify(); };

export const recordNewOrderPoll = ({ httpStatus, count, error }) => {
  state.newOrderPoll.lastPollAt = new Date().toISOString();
  state.newOrderPoll.lastHttpStatus = httpStatus ?? null;
  state.newOrderPoll.lastCount = count ?? null;
  state.newOrderPoll.lastError = error ? String(error).slice(0, 160) : null;
  notify();
};

export const recordChime = () => {
  state.newOrderPoll.lastChimeAt = new Date().toISOString();
  notify();
};

export const recordAutoPrintPoll = ({ httpStatus, printableCount, orderDetected, error }) => {
  state.autoPrint.lastPollAt = new Date().toISOString();
  state.autoPrint.lastHttpStatus = httpStatus ?? null;
  state.autoPrint.lastPrintableCount = printableCount ?? null;
  if (orderDetected) state.autoPrint.lastOrderDetected = orderDetected;
  if (error) state.autoPrint.lastPrintError = String(error).slice(0, 160);
  notify();
};

export const recordPrintAttempt = ({ orderId, success, error }) => {
  state.autoPrint.lastPrintAttemptAt = new Date().toISOString();
  state.autoPrint.lastPrintAttemptOrder = orderId;
  state.autoPrint.lastPrintResult = success ? 'SUCCESS' : 'FAILED';
  state.autoPrint.lastPrintError = success ? null : (error ? String(error).slice(0, 160) : null);
  notify();
};

export const recordResume = (reason) => {
  state.resume.lastResumeAt = new Date().toISOString();
  state.resume.lastResumeReason = reason;
  notify();
};
